import { ReactNode, useState } from 'react'
import { Box, IconButton, Paper, Popover, Skeleton, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

export interface StatTileProps {
  label: string
  value: ReactNode
  /** A second line under the value — a unit, a comparison, a count the value is derived from. */
  detail?: ReactNode
  /** Tints the value only, never the tile. A wall of coloured panels is unreadable. */
  severity?: 'success' | 'info' | 'warning' | 'error'
  /**
   * Shown behind an info button. Use it to say what the number actually measures. Click-to-open
   * rather than a hover tooltip: a phone has no hover, and a 14px glyph is not a tap target.
   */
  help?: string
  loading?: boolean
}

/**
 * One number at the top of the dashboard.
 *
 * The colour goes on the number and nothing else. An earlier pass tinted the whole tile and the
 * result was four coloured blocks competing for attention, which is the same as none of them
 * having it.
 */
export function StatTile({ label, value, detail, severity, help, loading }: StatTileProps) {
  const [helpAnchor, setHelpAnchor] = useState<HTMLElement | null>(null)

  return (
    <Paper
      sx={{
        p: { xs: 1.5, sm: 2 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minHeight: 24 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
        >
          {label}
        </Typography>
        {help && (
          <>
            {/* 40px hit area with negative margins so the button does not make the label row taller. */}
            <IconButton
              size="small"
              aria-label={`What ${label} measures`}
              onClick={(e) => setHelpAnchor(e.currentTarget)}
              sx={{ width: 40, height: 40, my: -1, mr: -1, color: 'text.disabled' }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <Popover
              open={helpAnchor != null}
              anchorEl={helpAnchor}
              onClose={() => setHelpAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              <Typography variant="body2" sx={{ p: 1.5, maxWidth: 280 }}>
                {help}
              </Typography>
            </Popover>
          </>
        )}
      </Box>

      {loading ? (
        <Skeleton variant="text" width="60%" height={40} />
      ) : (
        <Typography
          variant="h5"
          fontWeight="bold"
          color={severity ? `${severity}.main` : 'text.primary'}
          sx={{ lineHeight: 1.2 }}
        >
          {value}
        </Typography>
      )}

      {detail && !loading && (
        <Typography variant="caption" color="text.secondary">
          {detail}
        </Typography>
      )}
    </Paper>
  )
}
